/**
 * Whose setting is this? (#1680 — customization custody.)
 *
 * ── the defect ──────────────────────────────────────────────────────────────
 * `/settings?tab=regional` stacks two panels with no boundary between them:
 * `RegionalSettings`, which writes `core.user_setting` and is documented as
 * *"the user's OWN prefs … each user sets their own, RLS-scoped"*, and
 * `CurrencyRates`, which writes `set_fx_rate` for everybody. A person who
 * changes their timezone and then an exchange rate gets no signal that the
 * second one changed it for the whole deployment.
 *
 * When this was written the page also sat under a navigation group headed
 * **"This deployment"**, which said the opposite of the truth for the first of
 * those panels. It no longer renders from the console's settings page, which
 * subtracts the shell's own list and drops the emptied group — the label still
 * exists, and a deployment electing the grouped inner list would still show it.
 * Either way the heading was never the boundary: the two panels still stack,
 * and the boundary still has to be on the panels. Corrected here rather than
 * left standing, because a comment citing evidence a reader cannot find sends
 * them looking for a heading that is not on their screen.
 *
 * That is not a labelling nicety. The doors are cleanly scoped and the
 * SURFACES mix them: the same page also hosts "Personalize navigation", which
 * can write the deployment default, and a Branding panel whose density is
 * per-browser while its tokens are deployment-wide.
 *
 * ── why a component and not a sentence per page ────────────────────────────
 * Because there are four custodies and ten settings surfaces, and prose drifts.
 * One closed vocabulary means a reader learns the four phrases once and can
 * then trust them everywhere — the same argument `StateBoundary` makes for the
 * seven recipe states. A page that wants to say something else says it in its
 * own copy; it does not get to invent a fifth custody.
 */
import type { ReactNode } from 'react'
import s from './SettingsScope.module.css'

/** The four custodies the platform actually has doors for. */
export type SettingsCustody = 'user' | 'deployment' | 'persona' | 'portal'

/**
 * One sentence each, written for the person changing the setting rather than
 * for the schema. "Everyone" is the word that matters on `deployment`: it is
 * the difference nothing on the page was drawing — a panel writing for the
 * whole box sat beside one writing only for the reader, and neither said so.
 */
const SAYS: Record<SettingsCustody, { label: string; detail: string }> = {
  user: { label: 'Yours only', detail: 'These are your own preferences. Nobody else is affected.' },
  deployment: { label: 'Everyone here', detail: 'This changes the setting for everybody in this deployment.' },
  persona: { label: 'A role', detail: 'This is the default for everyone assigned this role. A person can still change their own.' },
  portal: { label: 'One portal', detail: 'This applies to the customers of this portal only.' },
}

export function SettingsScope({ custody, children }: {
  custody: SettingsCustody
  /** Anything the surface must add — never a replacement for the sentence. */
  children?: ReactNode
}) {
  const says = SAYS[custody]
  return (
    <p className={s.scope} data-custody={custody} data-testid={`settings-scope-${custody}`}>
      <strong className={s.who}>{says.label}</strong>
      <span className={s.detail}>{says.detail}</span>
      {children}
    </p>
  )
}
